import { Injectable, inject } from '@angular/core';
import { Center, CenterList, CenterMember } from '@app/core/models';
import { DrupalConstants, ViewOptions, ViewService } from '@makiwin/ngx-drupal8-rest';
import { Observable, map, mergeMap, of } from 'rxjs';
import { CustomService } from '../drupal8';

import { I18nService } from '@app/core/i18n/i18n.service';
import { ToolsService } from '../tools/tools.service';
import { TrackingService } from '../tracking/tracking.service';

export interface CenterForAutocomplete {
  id: number | string;
  label: string;
  isPro: boolean;
  objectValues: Center;
  picture: string;
}

@Injectable({
  providedIn: 'root',
})
export class CentersService {
  private viewService: ViewService = inject(ViewService);
  private tools: ToolsService = inject(ToolsService);
  private customService: CustomService = inject(CustomService);
  private i18nService: I18nService = inject(I18nService);
  private trackingService: TrackingService = inject(TrackingService);

  /**
   * Fetch centers from the API, filtered and paginatead by the viewOptions parameter.
   *
   * @param viewOptions
   *   View options to filter and paginate the results.
   * @returns
   *   OBSERVABLE: Array of centers.
   */
  getCenters({ viewOptions, url }: { viewOptions?: ViewOptions; url?: string } = {}): Observable<CenterList[]> {
    // if (!viewOptions?.filters?.search_api_language) {
    //   if (!viewOptions) {
    //     viewOptions = { filters: {} };
    //   }
    //   // TODO: to activate when the fallback for the language is ready
    //   // (viewOptions.filters as any) = {
    //   //   ...(viewOptions.filters ?? {}),
    //   //   ...{ search_api_language: [this.i18nService.language, 'en'] },
    //   // };
    //   viewOptions.filters = {
    //     ...(viewOptions.filters ?? {}),
    //     ...{ search_api_language: 'en' },
    //   };
    // }

    if (!viewOptions) {
      viewOptions = { filters: {} };
    }

    viewOptions.filters = {
      ...(viewOptions.filters ?? {}),
      ...{ langcode: 'en' },
    };

    url ??= '/api/v1.1/centers.json';
    return this.viewService.get(url, viewOptions).pipe(
      map((centers) =>
        centers.map((center) => {
          center.title = this.tools.encodeHTMLEntities(center.title);
          center.isPro = center.isPro === '1';
          center.url_alias = this.fixUrlCenter(center);
          return center;
        })
      )
    );
  }

  /**
   * Fetch centers from the API, to show in the maps.
   * @returns {Observable<any>}
   * Observable: Array of centers.
   */
  getCentersForMap(): Observable<CenterList[]> {
    return this.viewService
      .get('/api/v1/map_centers.json', {
        // TODO: to activate when the fallback for the language is ready
        filters: { langcode: 'en' },
      })
      .pipe(
        map((centers) =>
          centers.map((center) => {
            if (typeof center.field_school_logo === 'string' && center.field_school_logo.startsWith('/sites/')) {
              center.field_school_logo = DrupalConstants.backEndUrl + center.field_school_logo;
            }
            center.title = this.tools.encodeHTMLEntities(center.title);
            center.isPro = this.isPro(center);
            center.url_alias = this.fixUrlCenter(center);
            return center;
          })
        )
      );
  }

  /**
   * Fetch centers from the API for the autocomplete inputboxes, filtered and paginatead by the viewOptions parameter.
   *
   * @param viewOptions
   *   View options to filter and paginate the results.
   * @returns
   *   OBSERVABLE: Array of centers.
   */
  getCentersForAutoComplete({ url, viewOptions }: { url?: string; viewOptions?: ViewOptions }): Observable<any> {
    return this.getCenters({ url, viewOptions }).pipe(
      map((centers) =>
        centers.map((center: any) => ({
          id: center.nid,
          isPro: center.isPro ? center.isPro == '1' : this.isPro(center),
          // eslint-disable-next-line max-len
          label: `${center.title} (${center.field_school_address_locality} - ${center.field_school_address_country_code} )`,
          objectValues: center,
          picture: center.field_school_logo?.school_square_md,
        }))
      )
    );
  }

  /**
   * Retrieves centers for autocomplete based on the provided search text.
   *
   * @param params - The parameters for the search.
   * @param params.searchedText - The text to search for centers.
   * @param params.itemsPerPage - The number of items per page for pagination (default is 50).
   * @returns An Observable that emits the search results.
   */
  getCentersForAutoCompleteByText({
    searchedText,
    itemsPerPage = 50,
    url,
  }: {
    searchedText: string;
    itemsPerPage?: number;
    url?: string;
  }): Observable<any> {
    return this.getCentersForAutoComplete({
      url,
      viewOptions: {
        filters: { full_search: searchedText, country: searchedText },
        pagination: { items_per_page: itemsPerPage, page: 0 },
      },
    });
  }

  /**
   * Fetch center from the API for the autocomplete inputboxes, defined by id.
   *
   * @param id
   *   Id of the center.
   * @returns
   *   OBSERVABLE: Array of centers.
   */
  getCentersForAutoCompleteById(id: string | number): Observable<CenterForAutocomplete | null> {
    return this.getCenters({
      viewOptions: {
        filters: { full_search: id },
      },
    }).pipe(
      map((centers) => {
        if (centers.length === 0) {
          return null;
        }
        const autoCompleteCenters = centers.map((center: any) => ({
          id: center.nid,
          isPro: center.isPro ? center.isPro === '1' : this.isPro(center),
          // eslint-disable-next-line max-len
          label: `${center.title} (${center.field_school_address_locality} - ${center.field_school_address_country_code} )`,
          objectValues: center,
          picture: center.field_school_logo?.school_square_md,
        }));
        return autoCompleteCenters[0];
      })
    );
  }

  /**
   * Fetch center from the API defined by id.
   *
   * @param {string} id
   *   Id of the center.
   * @param {string} [langauge]
   *   Optional language to fetch the center details.
   * @returns {Observable<Center | CenterList | undefined>}
   *   Observable of Center or CenterList. Undefined if the center is not found.
   */
  getCenterById(id: string | number, language?: string): Observable<Center | CenterList | undefined> {
    return this.viewService
      .get('/api/v1/school_details.json', {
        args: [id.toString()],
        filters: { langcode: language ?? this.i18nService.language },
      })
      .pipe(
        mergeMap((centers) => {
          if (centers.length === 0 && this.i18nService.language !== 'en' && language !== 'en') {
            return this.getCenterById(id, 'en');
          }
          return of(centers?.[0]);
        }),
        map((center) => (center ? this.formatCenter(center) : undefined))
      );
  }

  /**
   * Determine if the center is a pro center.
   * @param center
   *  Center to check.
   * @returns
   * True if the center is a pro center, false otherwise.
   */
  isPro(center: CenterList | Center): boolean {
    return center.roles_target_id === 'School - Plus' || center.roles_target_id === 'School - Premium';
  }

  /**
   * Track the activity of the user for center detail.
   * @param center
   *  Center to track.
   */
  openCenter(center: CenterList | Center) {
    this.trackingService.trackEvent({
      event: 'center_open',
      dl_name: 'center_title',
      dl_value: center.title,
    });
    const data = { activity_type: 'view', entity_type: 'node', id: center.nid };
    this.customService.activityTracking(data).subscribe();
    // const url = center.url_alias ? center.url_alias : `/centers/${center.nid}`;
    // this.navDataService.navigate(url);
  }

  /**
   * Fix the url of the center for the router.
   * @param center
   * Center to fix the url.
   * @returns
   * Fixed url.
   */
  fixUrlCenter(center: CenterList | Center): string {
    let url_alias = (center.url_alias = center.url_alias ? center.url_alias : `/centers/${center.nid}`);

    if (url_alias.startsWith('/node')) {
      url_alias = url_alias.replace('/node', '/centers');
    }
    return decodeURI(url_alias);
  }

  getTeams(centerId: number | string): Observable<CenterMember[]> {
    return this.viewService.get('/api/v1/school_members.json', {
      args: [centerId.toString()],
      filters: { langcode: this.i18nService.language },
    });
  }

  private formatCenter(center: CenterList | Center): CenterList | Center {
    center.title = this.tools.encodeHTMLEntities(center.title);
    center.isPro = center.isPro ? (center as any).isPro === '1' : this.isPro(center);
    center.url_alias = this.fixUrlCenter(center);
    return center;
  }
}
